<template>
  <div ref="pageShell" class="pv4-page">

    <!-- Hero -->
    <header class="pv4-hero">
      <h1>Profile</h1>
      <p>管理您的个人身份与偏好设置。</p>
    </header>

    <div class="pv4-container">
      
      <!-- 左侧：身份侧栏 -->
      <aside class="pv4-sidebar" data-pv-animate>
        <div class="pv4-card pv4-identity">
          <div class="pv4-avatar-group">
            <div class="pv4-avatar-wrapper" @click="triggerAvatarUpload">
              <div v-if="currentUser.avatar" class="pv4-avatar-img">
                <img :src="currentUser.avatar" :alt="currentUser.name">
              </div>
              <div v-else class="pv4-avatar-fallback">{{ currentUser.name.charAt(0) }}</div>
              
              <!-- 悬停更换遮罩 -->
              <div class="pv4-avatar-overlay">
                <span class="material-symbols-outlined" style="font-size:20px">photo_camera</span>
              </div>
            </div>
            <input ref="avatarInput" class="pv4-hidden-input" type="file" accept="image/*" @change="handleAvatarChange">
          </div>
          
          <div class="pv4-user-info">
            <h1 class="pv4-name">{{ currentUser.name }}</h1>
            <span class="pv4-role">{{ currentUser.roleLabel }}</span>
          </div>
        </div>
      </aside>

      <!-- 右侧：内容主区 -->
      <main class="pv4-main">
        
        <!-- 资料区块 -->
        <section class="pv4-section" data-pv-animate>
          <header class="pv4-section-head">
            <div class="pv4-section-head__left">
              <span class="material-symbols-outlined">person</span>
              <div>
                <h2>基本资料</h2>
                <p>管理您的个人身份与联系方式</p>
              </div>
            </div>
            <div class="pv4-head-actions">
              <button class="pv4-btn-outline" @click="openPasswordDialog">
                <span class="material-symbols-outlined" style="font-size:16px">lock</span> 修改密码
              </button>
              <button class="pv4-btn-primary" :disabled="savingProfile" @click="saveProfile">
                <span class="material-symbols-outlined" style="font-size:16px">save</span> 保存修改
              </button>
            </div>
          </header>
          
          <div class="pv4-card pv4-form-card">
            <div class="pv4-form-grid">
              
              <div class="pv4-field">
                <label>用户名</label>
                <el-input v-model="profileForm.username" placeholder="用于登录的唯一账号" />
              </div>
              
              <div class="pv4-field">
                <label>昵称</label>
                <el-input v-model="profileForm.nickname" placeholder="您希望如何被称呼" />
              </div>
              
              <div class="pv4-field">
                <label>生日</label>
                <el-date-picker
                  v-model="profileForm.birthday"
                  type="date"
                  format="YYYY / MM / DD"
                  value-format="YYYY-MM-DD"
                  placeholder="选择特殊的那一天"
                  popper-class="pv4-date-popper"
                  style="width: 100%;"
                />
              </div>
              
              <div class="pv4-field">
                <label>邮箱</label>
                <el-input v-model="profileForm.email" placeholder="用于接收重要通知" />
              </div>
              
              <div class="pv4-field pv4-field--full">
                <label>手机号</label>
                <el-input v-model="profileForm.phone" placeholder="您的联系方式" />
              </div>

            </div>
          </div>
        </section>

        <!-- 通知区块 -->
        <section v-if="!isVisitor" class="pv4-section" data-pv-animate>
          <header class="pv4-section-head">
            <div class="pv4-section-head__left">
              <span class="material-symbols-outlined">notifications</span>
              <div>
                <h2>消息与提醒</h2>
                <p>定制您想接收的通知类型，修改后自动保存</p>
              </div>
            </div>
          </header>
          
          <div class="pv4-card pv4-notify-card">
            <div class="pv4-notify-list">
              
              <div 
                v-for="item in notificationItems" 
                :key="item.key" 
                class="pv4-notify-item"
              >
                <div class="pv4-notify-info">
                  <strong>{{ item.label }}</strong>
                  <span>{{ item.detail }}</span>
                </div>
                <div class="pv4-notify-action">
                  <el-switch
                    :model-value="notificationForm[item.key] === 1"
                    :loading="savingNotificationKey === item.key"
                    :disabled="Boolean(savingNotificationKey) && savingNotificationKey !== item.key"
                    inline-prompt active-text="开" inactive-text="关"
                    @change="(v) => saveNotificationToggle(item.key, v)"
                  />
                </div>
              </div>
              
            </div>
          </div>
        </section>

      </main>
    </div>

    <!-- 密码弹窗 -->
    <el-dialog v-model="passwordDialogVisible" width="400px" class="pv4-dialog" append-to-body>
      <div class="pv4-dialog-content">
        <h3 class="pv4-dialog-title">修改密码</h3>
        <p class="pv4-dialog-desc">为了您的账号安全，请定期更新密码。</p>
        
        <div class="pv4-dialog-form">
          <div class="pv4-field">
            <label>当前密码</label>
            <el-input v-model="passwordForm.oldPassword" show-password placeholder="输入当前密码" />
          </div>
          <div class="pv4-field">
            <label>新密码</label>
            <el-input v-model="passwordForm.newPassword" show-password placeholder="输入新密码" />
          </div>
        </div>
        
        <div class="pv4-dialog-footer">
          <button class="pv4-btn-outline" @click="closePasswordDialog">取消</button>
          <button class="pv4-btn-primary" :disabled="savingPassword" @click="savePassword">确认修改</button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import gsap from 'gsap'
import {
  getCurrentUser,
  getNotificationSetting,
  updateNotificationSetting,
  updateUserPassword,
  updateUserProfile,
  uploadUserAvatar
} from '../api/user'

const roleMap = { admin: '我的账号', partner: '她的账号', visitor: '访客' }

const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('us_space_user') || 'null') } catch { return null }
}
const isVisitor = computed(() => getStoredUser()?.role === 'visitor')
const persistUser = (payload) => {
  localStorage.setItem('us_space_user', JSON.stringify(payload || null))
  window.dispatchEvent(new Event('us-auth-changed'))
}

const profileForm = reactive({ username: '', nickname: '', email: '', phone: '', birthday: '' })
const passwordForm = reactive({ oldPassword: '', newPassword: '' })
const notificationForm = reactive({
  emailAnniversary: 1, emailStory: 1, emailPhoto: 1,
  emailTask: 1, emailMessage: 1, emailReport: 1
})

const notificationItems = [
  { key: 'emailAnniversary', label: '纪念日邮件提醒', detail: '重要日期临近时自动发送提醒邮件' },
  { key: 'emailStory', label: '故事更新提醒', detail: '新增故事记录后收到提醒' },
  { key: 'emailPhoto', label: '照片上传提醒', detail: '对方上传新照片时提醒你' },
  { key: 'emailTask', label: '任务打卡提醒', detail: '共同任务进度变化时通知' },
  { key: 'emailMessage', label: '悄悄话提醒', detail: '收到留言或私密消息时通知' },
  { key: 'emailReport', label: '报告生成提醒', detail: '月报、年报生成时发送提醒' }
]

const avatarInput = ref(null)
const pageShell = ref(null)
const profileUser = ref(getStoredUser())
const savingProfile = ref(false)
const uploadingAvatar = ref(false)
const savingPassword = ref(false)
const savingNotificationKey = ref('')
const passwordDialogVisible = ref(false)

const applyUserToForm = (p) => {
  profileForm.username = p?.username || ''
  profileForm.nickname = p?.nickname || ''
  profileForm.email = p?.email || ''
  profileForm.phone = p?.phone || ''
  profileForm.birthday = p?.birthday || ''
}
const applyNotificationToForm = (p = {}) => {
  notificationItems.forEach((i) => { notificationForm[i.key] = p[i.key] === 0 ? 0 : 1 })
}

const currentUser = computed(() => {
  const u = profileUser.value || {}
  return {
    name: u.nickname || u.username || '已登录用户',
    username: u.username || '未提供',
    role: u.role || 'member',
    roleLabel: roleMap[u.role] || '空间成员',
    avatar: u.avatar || ''
  }
})

const pulseElement = (selector) => {
  const element = pageShell.value?.querySelector(selector)
  if (!element) return
  gsap.fromTo(
    element,
    { y: 0, scale: 1 },
    {
      y: -4,
      scale: 1.02,
      duration: 0.2,
      ease: 'power2.out',
      yoyo: true,
      repeat: 1
    }
  )
}

/* ── 动画：干净利落的自下而上入场 ── */
const animateEntrance = () => {
  nextTick(() => {
    const sidebar = pageShell.value?.querySelector('.pv4-sidebar')
    const sections = pageShell.value?.querySelectorAll('.pv4-section')
    const rows = pageShell.value?.querySelectorAll('.pv4-notify-item')
    if (sidebar) gsap.killTweensOf(sidebar)
    if (sections?.length) gsap.killTweensOf(sections)
    if (rows?.length) gsap.killTweensOf(rows)
    if (sidebar) {
      gsap.fromTo(
        sidebar,
        { opacity: 0, x: -24, y: 24 },
        { opacity: 1, x: 0, y: 0, duration: 0.8, ease: 'power3.out' }
      )
    }
    if (sections?.length) {
      gsap.fromTo(
        sections,
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.12, ease: 'power3.out', delay: 0.08 }
      )
    }
    if (rows?.length) {
      gsap.fromTo(
        rows,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.06, ease: 'power2.out', delay: 0.3 }
      )
    }
  })
}

/* ── 接口交互 ── */
const fetchProfile = async () => {
  const user = await getCurrentUser()
  profileUser.value = user
  applyUserToForm(user)
  persistUser(user)
}
const fetchNotification = async () => {
  const settings = await getNotificationSetting()
  applyNotificationToForm(settings)
}

const openPasswordDialog = () => { passwordDialogVisible.value = true }
const closePasswordDialog = () => {
  passwordDialogVisible.value = false
  passwordForm.oldPassword = ''
  passwordForm.newPassword = ''
}

const saveProfile = async () => {
  savingProfile.value = true
  try {
    const payload = {
      username: profileForm.username.trim() || undefined,
      nickname: profileForm.nickname.trim() || undefined,
      email: profileForm.email.trim() || undefined,
      phone: profileForm.phone.trim() || undefined,
      birthday: profileForm.birthday || undefined
    }
    const user = await updateUserProfile(payload)
    profileUser.value = user
    applyUserToForm(user)
    persistUser(user)
    pulseElement('.pv4-form-card')
    ElMessage.success('个人资料已更新')
  } catch (error) {
    ElMessage.error(error.message || '保存资料失败')
  } finally {
    savingProfile.value = false
  }
}

const triggerAvatarUpload = () => {
  if (uploadingAvatar.value) return
  avatarInput.value?.click() 
}

const handleAvatarChange = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return
  uploadingAvatar.value = true
  try {
    await uploadUserAvatar(file)
    await fetchProfile()
    pulseElement('.pv4-avatar-wrapper')
    ElMessage.success('头像已更新')
  } catch (error) {
    ElMessage.error(error.message || '头像上传失败')
  } finally {
    event.target.value = ''
    uploadingAvatar.value = false
  }
}

const savePassword = async () => {
  if (!passwordForm.oldPassword.trim()) { ElMessage.warning('请输入当前密码'); return }
  if (!passwordForm.newPassword.trim()) { ElMessage.warning('请输入新密码'); return }
  savingPassword.value = true
  try {
    await updateUserPassword({ oldPassword: passwordForm.oldPassword, newPassword: passwordForm.newPassword })
    closePasswordDialog()
    pulseElement('.pv4-head-actions')
    ElMessage.success('密码已更新')
  } catch (error) {
    ElMessage.error(error.message || '修改密码失败')
  } finally {
    savingPassword.value = false
  }
}

const saveNotificationToggle = async (key, value) => {
  const prev = notificationForm[key]
  notificationForm[key] = value ? 1 : 0
  savingNotificationKey.value = key
  try {
    const settings = await updateNotificationSetting({ ...notificationForm })
    applyNotificationToForm(settings)
    pulseElement('.pv4-notify-card')
  } catch (error) {
    notificationForm[key] = prev
    ElMessage.error(error.message || '保存通知设置失败')
  } finally {
    savingNotificationKey.value = ''
  }
}

onMounted(async () => {
  const tasks = [fetchProfile()]
  if (!isVisitor.value) tasks.push(fetchNotification())
  const results = await Promise.allSettled(tasks)
  animateEntrance()
  if (results.some((r) => r.status === 'rejected')) {
    const first = results.find((r) => r.status === 'rejected')
    ElMessage.error(first?.reason?.message || '加载个人中心失败')
  }
})

onBeforeUnmount(() => {
  const sidebar = pageShell.value?.querySelector('.pv4-sidebar')
  const sections = pageShell.value?.querySelectorAll('.pv4-section')
  const rows = pageShell.value?.querySelectorAll('.pv4-notify-item')
  const formCard = pageShell.value?.querySelector('.pv4-form-card')
  const avatar = pageShell.value?.querySelector('.pv4-avatar-wrapper')
  const actions = pageShell.value?.querySelector('.pv4-head-actions')
  const notifyCard = pageShell.value?.querySelector('.pv4-notify-card')
  if (sidebar) gsap.killTweensOf(sidebar)
  if (sections?.length) gsap.killTweensOf(sections)
  if (rows?.length) gsap.killTweensOf(rows)
  if (formCard) gsap.killTweensOf(formCard)
  if (avatar) gsap.killTweensOf(avatar)
  if (actions) gsap.killTweensOf(actions)
  if (notifyCard) gsap.killTweensOf(notifyCard)
  const cleanupTargets = [
    sidebar,
    ...(sections ? Array.from(sections) : []),
    ...(rows ? Array.from(rows) : []),
    formCard,
    avatar,
    actions,
    notifyCard
  ].filter(Boolean)
  if (cleanupTargets.length) {
    gsap.set(cleanupTargets, { clearProps: 'transform,opacity,filter' })
  }
})
</script>

<style scoped>
/* ═══════ Design Tokens ═══════ */
.pv4-page {
  --pv-bg: #fff8f5;
  --pv-surface: #f5ece8;
  --pv-surface-high: #efe6e2;
  --pv-surface-low: #fbf2ed;
  --pv-primary: #7b5455;
  --pv-primary-container: #f4c2c2;
  --pv-on-surface: #1f1b18;
  --pv-on-surface-variant: #504444;
  --pv-outline: #827473;
  --pv-outline-variant: #d4c2c2;
  --pv-radius: 12px;
  font-family: 'Plus Jakarta Sans', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 40px 64px;
  min-height: 100vh;
}

.material-symbols-outlined {
  font-variation-settings: 'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24;
  vertical-align: middle;
}

/* ═══════ Hero ═══════ */
.pv4-hero {
  text-align: center;
  padding: 32px 0 24px;
}
.pv4-hero h1 {
  font-family: 'Noto Serif', serif;
  font-size: 48px; font-weight: 400; line-height: 1.2;
  color: var(--pv-primary);
  margin: 0 0 8px;
}
.pv4-hero p {
  font-size: 18px; line-height: 1.6;
  color: var(--pv-on-surface-variant);
  max-width: 600px; margin: 0 auto;
}

/* ═══════ Container grid ═══════ */
.pv4-container {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

/* ═══════ Sidebar ═══════ */
.pv4-sidebar {
  position: sticky;
  top: 80px;
}

/* ═══════ Card base ═══════ */
.pv4-card {
  background: #fff;
  border: 1px solid rgba(212,194,194,0.3);
  border-radius: var(--pv-radius);
  padding: 24px;
  box-shadow: 0 20px 40px rgba(123,84,85,0.05);
  transition: box-shadow 0.3s;
}
.pv4-card:hover {
  box-shadow: 0 20px 40px rgba(123,84,85,0.08);
}

/* ═══════ Identity card ═══════ */
.pv4-identity {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  padding: 32px 24px;
}

.pv4-avatar-group { flex-shrink: 0; }

.pv4-avatar-wrapper {
  position: relative;
  width: 88px; height: 88px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(123,84,85,0.15);
  transition: transform 0.3s;
}
.pv4-avatar-wrapper:hover { transform: scale(1.05); }

.pv4-avatar-img img {
  width: 100%; height: 100%;
  object-fit: cover; display: block;
}

.pv4-avatar-fallback {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background: var(--pv-primary);
  font-size: 28px; font-weight: 700; color: #fff;
}

.pv4-avatar-overlay {
  position: absolute; inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity 0.25s;
  border-radius: 50%;
  color: #fff;
}
.pv4-avatar-wrapper:hover .pv4-avatar-overlay { opacity: 1; }

.pv4-hidden-input { display: none; }

.pv4-user-info { min-width: 0; }
.pv4-name {
  margin: 0;
  font-family: 'Noto Serif', serif;
  font-size: 22px; font-weight: 500;
  color: var(--pv-on-surface);
}
.pv4-role {
  display: inline-block;
  margin-top: 6px;
  font-size: 13px; font-weight: 600; letter-spacing: 0.05em;
  color: var(--pv-on-surface-variant);
  background: var(--pv-surface);
  padding: 3px 12px; border-radius: 999px;
}

/* ═══════ Main ═══════ */
.pv4-main {
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
}

/* ═══════ Section ═══════ */
.pv4-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pv4-section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}
.pv4-section-head__left {
  display: flex; align-items: center; gap: 12px;
}
.pv4-section-head__left > .material-symbols-outlined {
  font-size: 24px; color: var(--pv-primary);
}
.pv4-section-head h2 {
  margin: 0;
  font-family: 'Noto Serif', serif;
  font-size: 20px; font-weight: 500;
  color: var(--pv-on-surface);
}
.pv4-section-head p {
  margin: 2px 0 0;
  font-size: 13px; color: var(--pv-on-surface-variant); line-height: 1.5;
}

.pv4-head-actions {
  display: flex; gap: 10px; flex-shrink: 0;
}

/* ═══════ Shared buttons ═══════ */
.pv4-btn-outline {
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid var(--pv-surface-high);
  background: var(--pv-surface-low);
  color: var(--pv-on-surface-variant);
  font-size: 13px; font-weight: 600;
  padding: 8px 18px; border-radius: 999px;
  cursor: pointer; transition: all 0.2s;
  font-family: inherit;
}
.pv4-btn-outline:hover {
  background: var(--pv-primary-container);
  border-color: var(--pv-primary-container);
  color: var(--pv-primary);
}
.pv4-btn-primary {
  display: inline-flex; align-items: center; gap: 4px;
  border: none;
  background: var(--pv-primary-container);
  color: var(--pv-primary);
  font-size: 13px; font-weight: 600; letter-spacing: 0.05em;
  padding: 8px 18px; border-radius: 999px;
  cursor: pointer; transition: all 0.2s;
  font-family: inherit;
}
.pv4-btn-primary:hover {
  background: var(--pv-primary);
  color: #fff;
}
.pv4-btn-primary:disabled {
  opacity: 0.6; cursor: not-allowed;
}

/* ═══════ Form card ═══════ */
.pv4-form-card {
  padding: 28px;
}
.pv4-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}
.pv4-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pv4-field label {
  font-size: 13px; font-weight: 600; color: var(--pv-on-surface-variant);
}
.pv4-field--full {
  grid-column: 1 / -1;
}

/* ═══════ Notify card ═══════ */
.pv4-notify-card {
  padding: 8px 0;
}
.pv4-notify-list {
  display: flex;
  flex-direction: column;
}
.pv4-notify-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(212,194,194,0.2);
  transition: background 0.2s;
}
.pv4-notify-item:last-child { border-bottom: none; }
.pv4-notify-item:hover { background: var(--pv-surface-low); }

.pv4-notify-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.pv4-notify-info strong {
  font-size: 14px; font-weight: 600; color: var(--pv-on-surface);
}
.pv4-notify-info span {
  font-size: 12px; color: var(--pv-on-surface-variant); line-height: 1.5;
}
.pv4-notify-action { flex-shrink: 0; }

/* ═══════ Dialog ═══════ */
.pv4-dialog-content {
  display: flex; flex-direction: column; gap: 16px;
}
.pv4-dialog-title {
  margin: 0;
  font-family: 'Noto Serif', serif;
  font-size: 20px; font-weight: 500;
  color: var(--pv-on-surface);
}
.pv4-dialog-desc {
  margin: 0;
  font-size: 14px; color: var(--pv-on-surface-variant); line-height: 1.5;
}
.pv4-dialog-form {
  display: flex; flex-direction: column; gap: 16px;
}
.pv4-dialog-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding-top: 8px;
}

/* ═══════ Responsive ═══════ */
@media (max-width: 900px) {
  .pv4-container {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  .pv4-sidebar { position: static; }
  .pv4-identity {
    flex-direction: row;
    text-align: left;
    padding: 24px;
  }
}
@media (max-width: 768px) {
  .pv4-page { padding: 0 16px 40px; }
  .pv4-hero h1 { font-size: 34px; }
  .pv4-hero p { font-size: 15px; }
  .pv4-section-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  .pv4-head-actions { width: 100%; }
  .pv4-form-grid { grid-template-columns: 1fr; }
  .pv4-field--full { grid-column: span 1; }
}
@media (max-width: 480px) {
  .pv4-hero h1 { font-size: 28px; }
  .pv4-identity {
    flex-direction: column;
    text-align: center;
    padding: 24px 20px;
  }
  .pv4-form-card,
  .pv4-notify-card { padding: 20px; }
  .pv4-notify-item { padding: 14px 16px; }
}
</style>
